import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_KEYS,
  type AuthSnapshot,
  clearAuth,
  hasToken,
  persistLogin,
  type LoginResponseLike,
  readAuthSnapshot,
} from '@/shared/auth/storage';
import { setUnauthorizedHandler } from '@/shared/api/client';

interface AuthContextValue {
  snapshot: AuthSnapshot;
  isAuthenticated: boolean;
  loginFromResponse: (res: LoginResponseLike) => void;
  logout: () => void;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<AuthSnapshot>(() => readAuthSnapshot());

  const refresh = useCallback(() => setSnapshot(readAuthSnapshot()), []);

  const logout = useCallback(() => {
    clearAuth();
    refresh();
    navigate('/login', { replace: true });
  }, [navigate, refresh]);

  const loginFromResponse = useCallback(
    (res: LoginResponseLike) => {
      persistLogin(res);
      refresh();
    },
    [refresh],
  );

  // 401 ирэхэд api/client.ts-ээс нэмэгдсэн handler ажиллана.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearAuth();
      refresh();
      navigate('/login', { replace: true });
    });
  }, [navigate, refresh]);

  // Бусад tab-д logout/login хийхэд sync.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key && (AUTH_KEYS as readonly string[]).includes(e.key)) refresh();
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      snapshot,
      isAuthenticated: !!snapshot.token,
      loginFromResponse,
      logout,
      refresh,
    }),
    [snapshot, loginFromResponse, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function useIsAuthenticated(): boolean {
  return hasToken();
}
